import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import BadgeIcon from '@mui/icons-material/Badge'
import BusinessIcon from '@mui/icons-material/Business'
import PhoneIcon from '@mui/icons-material/Phone'
import HistoryIcon from '@mui/icons-material/History'
import SupportPatientBookingHistory from './components/SupportPatientBookingHistory'

function fallback(value, empty = '-') {
  return value == null || value === '' ? empty : value
}

function getBrandName(patient) {
  return (
    patient?.brandData?.brandName ||
    patient?.brandData?.title ||
    patient?.clinicBrandData?.brandName ||
    patient?.clinicBrandData?.title ||
    patient?.clinicBrandName ||
    patient?.brandName ||
    (patient?.clinicBrandId ? `Brand ${patient.clinicBrandId}` : 'Unknown brand')
  )
}

function getClinicName(patient) {
  return (
    patient?.clinicData?.title ||
    patient?.clinicData?.name ||
    patient?.clinicLocation?.title ||
    patient?.clinicLocationName ||
    patient?.clinicName ||
    patient?.clinicId ||
    ''
  )
}

function getBrandKey(patient) {
  return patient?.clinicBrandId || getBrandName(patient)
}

function groupPatientsByBrand(patients) {
  const map = new Map()
  ;(patients || []).forEach((patient) => {
    const key = getBrandKey(patient)
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: getBrandName(patient),
        patients: [],
      })
    }
    map.get(key).patients.push(patient)
  })
  return Array.from(map.values())
}

function PatientSummaryCard({ patient, selected, onClick }) {
  const clinicName = getClinicName(patient)
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        cursor: 'pointer',
        borderColor: selected ? '#6779e3' : 'divider',
        backgroundColor: selected ? 'rgba(103, 121, 227, 0.06)' : '#fff',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            backgroundColor: 'rgba(103, 121, 227, 0.1)',
            color: '#6779e3',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <AccountCircleIcon />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {fallback(patient?.name, 'Unknown patient')}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.75 }}>
            {patient?.patientNumber ? (
              <Chip size="small" icon={<BadgeIcon sx={{ fontSize: 14 }} />} label={`#${patient.patientNumber}`} />
            ) : null}
            {patient?.mobile ? (
              <Chip size="small" icon={<PhoneIcon sx={{ fontSize: 14 }} />} label={patient.mobile} />
            ) : null}
            {patient?.requiresValidation ? (
              <Chip size="small" color="warning" label="Needs validation" />
            ) : null}
          </Stack>
          {(getBrandName(patient) || clinicName) ? (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              <BusinessIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {[getBrandName(patient), clinicName].filter(Boolean).join(' - ')}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  )
}

function DetailLine({ label, value }) {
  if (value == null || value === '') return null
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
        {String(value)}
      </Typography>
    </Box>
  )
}

export default function CareOrchestrationPatientMatchDrawer({
  open,
  lead,
  patients = [],
  onClose,
}) {
  const groups = useMemo(() => groupPatientsByBrand(patients), [patients])
  const [brandKey, setBrandKey] = useState('')
  const [selectedPatientId, setSelectedPatientId] = useState('')

  useEffect(() => {
    if (!open) return
    const firstGroup = groups[0]
    setBrandKey(firstGroup?.key || '')
    setSelectedPatientId(firstGroup?.patients?.[0]?.id || '')
  }, [groups, open])

  const selectedGroup = groups.find((group) => group.key === brandKey) || groups[0] || null
  const selectedPatient =
    selectedGroup?.patients?.find((patient) => patient.id === selectedPatientId) ||
    selectedGroup?.patients?.[0] ||
    null

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', md: 780 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="overline" color="text.secondary">
              Existing patient match
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {fallback(lead?.name, 'Lead')} {lead?.phone ? `- ${lead.phone}` : ''}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {patients.length} PatientInfo {patients.length === 1 ? 'record' : 'records'} matched this phone number.
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close patient match drawer">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {groups.length > 1 ? (
        <Tabs
          value={selectedGroup?.key || false}
          onChange={(_, nextKey) => {
            const nextGroup = groups.find((group) => group.key === nextKey)
            setBrandKey(nextKey)
            setSelectedPatientId(nextGroup?.patients?.[0]?.id || '')
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ px: 2, borderBottom: '1px solid', borderColor: 'divider' }}
        >
          {groups.map((group) => (
            <Tab
              key={group.key}
              value={group.key}
              label={`${group.label} (${group.patients.length})`}
              sx={{ textTransform: 'none' }}
            />
          ))}
        </Tabs>
      ) : null}

      <Box sx={{ flex: 1, overflow: 'auto', p: 2.5 }}>
        {selectedGroup ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Patient records in {selectedGroup.label}
              </Typography>
              <Stack spacing={1}>
                {selectedGroup.patients.map((patient) => (
                  <PatientSummaryCard
                    key={patient.id || patient.patientNumber || patient.mobile}
                    patient={patient}
                    selected={patient.id === selectedPatient?.id}
                    onClick={() => setSelectedPatientId(patient.id)}
                  />
                ))}
              </Stack>
            </Box>

            {selectedPatient ? (
              <>
                <Divider />
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: '#fbfdff' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                    Patient details
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                      gap: 1.5,
                    }}
                  >
                    <DetailLine label="Patient ID" value={selectedPatient.id} />
                    <DetailLine label="Patient number" value={selectedPatient.patientNumber} />
                    <DetailLine label="UH ID" value={selectedPatient.uhId} />
                    <DetailLine label="Status" value={selectedPatient.status} />
                    <DetailLine label="Clinic brand" value={getBrandName(selectedPatient)} />
                    <DetailLine label="Clinic" value={getClinicName(selectedPatient)} />
                    <DetailLine label="Email" value={selectedPatient.email} />
                    <DetailLine label="Mobile" value={selectedPatient.mobile} />
                  </Box>
                </Paper>

                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                    <HistoryIcon sx={{ color: '#6779e3' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Booking history
                    </Typography>
                  </Stack>
                  <SupportPatientBookingHistory
                    patientId={selectedPatient.id}
                    clinicId={selectedPatient.clinicId}
                    emptyMessage="No booking history found for this matched patient."
                  />
                </Box>
              </>
            ) : null}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No matching patient records found.
          </Typography>
        )}
      </Box>
    </Drawer>
  )
}
